import React, { useState } from 'react';
import { History } from 'lucide-react';
import { useQuickStartAssistant } from '../../contexts/QuickStartAssistantContext';

interface ConversationHistoryMenuProps {
  /** 'light' matches the modal's gray-icon header; 'onDark' matches the bubble's colored header (white icon, opacity hover). */
  theme?: 'light' | 'onDark';
}

/**
 * The History toggle + dropdown (start a new chat, or switch to a past
 * conversation) — shared by the Quick Start modal and the floating bubble so
 * both hosts offer the same explicit "New chat" control, distinct from
 * closing the window (see SupportChatWindow: closing must not clear the
 * conversation).
 */
const ConversationHistoryMenu: React.FC<ConversationHistoryMenuProps> = ({ theme = 'light' }) => {
  const { conversations, loadConversations, switchConversation, conversationId, startNewConversation } = useQuickStartAssistant();
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => {
    if (!isOpen) loadConversations();
    setIsOpen((v) => !v);
  };

  const buttonClass = theme === 'onDark'
    ? 'hover:opacity-75 transition-opacity'
    : 'p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';

  return (
    <div className="relative flex items-center">
      <button onClick={toggle} aria-label="Conversation history" title="Conversation history" className={buttonClass}>
        <History className={theme === 'onDark' ? 'w-4 h-4' : 'w-4 h-4'} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-72 max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-10 text-left">
          <button
            onClick={() => { startNewConversation(); setIsOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm font-medium text-primary hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700"
          >
            + New chat
          </button>
          {conversations.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">No past conversations yet.</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.id}
                onClick={() => { switchConversation(c.id); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm truncate hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  c.id === conversationId ? 'bg-gray-50 dark:bg-gray-700 font-medium' : 'text-gray-700 dark:text-gray-200'
                }`}
                title={c.title || 'Untitled conversation'}
              >
                {c.source === 'email' && !(c.title || '').startsWith('📧') && <span className="mr-1" aria-label="From email">📧</span>}
                {c.source === 'task' && !(c.title || '').startsWith('🤖') && <span className="mr-1" aria-label="Agent run">🤖</span>}
                {c.title || 'Untitled conversation'}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationHistoryMenu;
